import React from 'react';
import ReactMde from 'react-mde';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Tooltip, TrevorIcon } from '@trevor/ui-components';
import TextBoxRef, {
  CENTER,
  LEFT,
  RIGHT,
  TextAlign,
} from '../../../util/TextboxRef';
import { converter } from '../converter';
import { EditableTextBoxProps } from '../types';
import styles from './EditableTextBox.module.scss';
import {
  TEXT_WIDGET_ALIGN_CENTER_BUTTON,
  TEXT_WIDGET_DONE_BUTTON,
} from '../../../constants/test-ids';

const DEFAULT_HEIGHT = 50;
const DEFAULT_WIDTH = 300;
const WIDTH_BREAKPOINT = 200;

const generateMarkdownPreview = (markdown: string) =>
  Promise.resolve(converter.makeHtml(markdown));

const getTextBoxSize = (editor: HTMLDivElement | null): [number, number] => {
  // get the height of the header (we don't have access to this so can't use a ref)
  const header = editor?.querySelector('.mde-header');

  if (editor && header) {
    // the -3 enables you to see the border
    return [editor.clientHeight - header.clientHeight - 3, editor.clientWidth];
  }

  return [DEFAULT_HEIGHT, DEFAULT_WIDTH];
};

// https://github.com/andrerpena/react-mde/blob/master/src/commands/default-commands/defaults.ts
const MDE_TOOLBAR_COMMANDS = [
  [
    'header',
    'italic',
    'bold',
    'link',
    'quote',
    'unordered-list',
    'ordered-list',
  ],
];

const MDE_CHILD_PROPS = {
  textArea: {
    placeholder: 'Write something here',
    autoFocus: true,
  },
};

export const EditableTextBox: React.FC<EditableTextBoxProps> = ({
  index,
  widget,
  onExitEditMode,
  onUpdate,
}) => {
  const [textAlign, setTextAlign] = React.useState<TextAlign>(widget.textAlign);
  const [tab, setTab] = React.useState<'write' | 'preview'>('write');
  const [height, setHeight] = React.useState(DEFAULT_HEIGHT);
  const [width, setWidth] = React.useState(DEFAULT_WIDTH);
  const [value, setValue] = React.useState(widget.value ?? '');

  const textContentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const [newHeight, newWidth] = getTextBoxSize(textContentRef.current);
    if (widget.width !== width || widget.height !== height) {
      setHeight(newHeight);
      setWidth(newWidth);
    }
  }, [widget]);

  const handleTextAlign = (newTextAlign: TextAlign) => () =>
    setTextAlign(newTextAlign);

  const handleCancel = () => {
    setValue(widget.value ?? '');
    setTextAlign(widget.textAlign);
    onExitEditMode();
  };

  const handleUpdate = () => {
    onUpdate(TextBoxRef.create(value, textAlign));
    onExitEditMode();
  };

  const isSmall = width < WIDTH_BREAKPOINT;

  return (
    <>
      <div
        className={`${styles.reactMDEContainer} ${styles[textAlign]}`}
        ref={textContentRef}
        style={{ textAlign }}
      >
        <ReactMde
          key={`${index}-${height}`} // without key height doesn't update
          value={value}
          onChange={setValue}
          selectedTab={tab}
          onTabChange={setTab}
          generateMarkdownPreview={generateMarkdownPreview}
          toolbarCommands={MDE_TOOLBAR_COMMANDS}
          minEditorHeight={height}
          heightUnits="px"
          childProps={MDE_CHILD_PROPS}
        />
      </div>
      <div className={styles.actionButtonsContainer}>
        <div className={styles.actionButtonsContainerInner}>
          {isSmall ? (
            <div>
              <Button
                variant="secondary"
                size="small"
                data-tooltip-id="cancel"
                onClick={handleCancel}
                icon={<TrevorIcon name="close" />}
              />
              <Tooltip id="cancel">Cancel</Tooltip>
            </div>
          ) : (
            <Button variant="secondary" size="small" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <div
            className={
              isSmall ? styles.alignButtonsGroupSmall : styles.alignButtonsGroup
            }
          >
            <button
              onClick={handleTextAlign(LEFT)}
              className={styles.alignButton}
            >
              <div>
                <span data-tooltip-id={`${index}_left`}>
                  <span>
                    <FontAwesomeIcon icon="align-left" />
                  </span>
                </span>
                <Tooltip id={`${index}_left`}>Align text left</Tooltip>
              </div>
            </button>
            <button
              onClick={handleTextAlign(CENTER)}
              className={styles.alignButton}
              data-testid={TEXT_WIDGET_ALIGN_CENTER_BUTTON}
            >
              <div>
                <span data-tooltip-id={`${index}_center`}>
                  <span>
                    <FontAwesomeIcon icon="align-center" />
                  </span>
                </span>
                <Tooltip id={`${index}_center`}>Align text center</Tooltip>
              </div>
            </button>
            <button
              onClick={handleTextAlign(RIGHT)}
              className={styles.alignButton}
            >
              <div>
                <span data-tooltip-id={`${index}_right`}>
                  <span>
                    <FontAwesomeIcon icon="align-right" />
                  </span>
                </span>
                <Tooltip id={`${index}_right`}>Align text right</Tooltip>
              </div>
            </button>
          </div>
          {isSmall ? (
            <div>
              <Button
                size="small"
                data-tooltip-id="done"
                data-testid={TEXT_WIDGET_DONE_BUTTON}
                onClick={handleUpdate}
                icon={<TrevorIcon name="check-circle" />}
              />
              <Tooltip id="done">Done</Tooltip>
            </div>
          ) : (
            <Button
              size="small"
              data-testid={TEXT_WIDGET_DONE_BUTTON}
              onClick={handleUpdate}
            >
              Done
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
